/**
 * Helpers for the site Transactions tab and the Overview revenue cards
 * (castr `GET /sites/:id/transactions*`, internal/sites-control-plane-api.md §6e).
 */
import { parseRef } from '@/Utils/siteAccess'

/** Paywall invoice statuses castr passes through. */
export const TX_STATUSES = ['pending', 'active', 'expired', 'refund', 'chargeback']
export const TX_STATUS_LABELS = { pending: 'Pending', active: 'Active', expired: 'Expired', refund: 'Refunded', chargeback: 'Chargeback' }
// StatusPill only knows success | muted | warning | accent.
export const TX_STATUS_VARIANTS = { pending: 'muted', active: 'success', expired: 'muted', refund: 'warning', chargeback: 'warning' }

/** Date-range presets for the Transactions tab (`from`/`to` are inclusive UTC days, §6e). */
export const DAY_MS = 86_400_000
export const RANGE_PRESETS = [
	{ id: 'all', label: 'All time', days: null },
	{ id: '7d', label: '7 days', days: 7 },
	{ id: '30d', label: '30 days', days: 30 },
	{ id: '90d', label: '90 days', days: 90 },
	{ id: 'custom', label: 'Custom', days: null },
]
/** `YYYY-MM-DD` of a timestamp in UTC. */
export const isoDay = (ms = Date.now()) => new Date(ms).toISOString().slice(0, 10)
/** `{ from, to }` for a preset id (`all` / `custom` → both undefined). */
export function presetRange(id, now = Date.now()) {
	const preset = RANGE_PRESETS.find((p) => p.id === id)
	if (!preset?.days) return { from: undefined, to: undefined }
	return { from: isoDay(now - (preset.days - 1) * DAY_MS), to: isoDay(now) }
}

/** `12.5, 'USD'` → "$12.50"; `null` → "—"; unknown currency → "12.50 XYZ". */
export function formatMoney(amount, currency) {
	if (amount === null || amount === undefined || amount === '') return '—'
	const n = Number(amount)
	if (!Number.isFinite(n)) return String(amount)
	const code = String(currency || 'USD').toUpperCase()
	try {
		return n.toLocaleString(undefined, { style: 'currency', currency: code })
	} catch {
		return `${n.toFixed(2)} ${code}`
	}
}

/**
 * The Summary's gross/net are null when the site sold in more than one
 * currency; the cards then list `by_currency` instead.
 * @returns {Array<{ currency: string, gross: number, refunded: number, net: number, count: number }>}
 */
export function currencyLines(summary) {
	if (!summary) return []
	if (summary.currency && summary.currency !== 'mixed') {
		return [{ currency: summary.currency, gross: summary.gross, refunded: summary.refunded, net: summary.net, count: summary.count }]
	}
	return Object.entries(summary.by_currency || {}).map(([currency, v]) => ({ currency, ...v }))
}

/**
 * Only one full refund of the newest paid payment is possible (castr §6e), so
 * a transaction is refundable while it still holds collected money and that
 * payment is paid.
 */
export function refundablePayment(tx) {
	if (!tx || tx.free) return null
	if (['refund', 'chargeback', 'pending'].includes(tx.status)) return null
	if (!(Number(tx.net_amount) > 0)) return null
	const payment = tx.payment && tx.payment.status === 'paid' ? tx.payment : (tx.payments || []).filter((p) => p.status === 'paid').sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
	if (!payment) return null
	const alreadyRefunded = (tx.refunds || []).some((r) => r.payment_transaction_id && payment.transaction_id && r.payment_transaction_id === payment.transaction_id)
	return alreadyRefunded ? null : payment
}

/**
 * castr's `refundable` flag (2026-09-30: true only while a `paid` payment
 * exists) when the row carries it, else the local heuristic above.
 */
export function isRefundable(tx) {
	if (!tx) return false
	if (typeof tx.refundable === 'boolean') return tx.refundable
	return !!refundablePayment(tx)
}

/** The payment `POST …/refund` picks by default: the newest `paid` one. */
export function refundTarget(tx) {
	if (!tx) return null
	const fromHeuristic = refundablePayment(tx)
	if (fromHeuristic) return fromHeuristic
	const paid = (tx.payments || []).filter((p) => p.status === 'paid').sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
	return paid[0] || (tx.payment?.status === 'paid' ? tx.payment : null)
}

/** A checkout the viewer never finished: nothing collected, `price` = what would have been charged. */
export const isIncomplete = (tx) => !!tx && tx.status === 'pending' && !tx.free

/** Why a row has no Refund action (tooltip copy). */
export function unrefundableReason(tx) {
	if (!tx) return ''
	if (tx.free) return 'Free grant — nothing to refund'
	if (isIncomplete(tx)) return 'Checkout not completed — nothing was charged'
	if (tx.status === 'chargeback') return 'Charged back by the card issuer'
	if (tx.status === 'refund' || (Number(tx.amount) > 0 && Number(tx.net_amount) <= 0)) return 'Already refunded in full'
	return 'No paid payment to refund'
}

/** Amount of `payment` (falls back to the invoice `amount`). */
export function paymentAmount(tx, payment) {
	const n = Number(payment?.amount ?? tx?.amount)
	return Number.isFinite(n) ? n : 0
}

/**
 * `item:12` / `category:3` → a readable label using the content and category
 * lists the dashboard already has (`summary.top_content[].title` is null on castr).
 */
export function refLabel(ref, { content = [], categories = [] } = {}) {
	const parsed = parseRef(ref)
	if (!parsed) return String(ref || '')
	const list = parsed.kind === 'category' ? categories : content
	const found = list.find((x) => String(x.id) === String(parsed.id))
	if (found?.title) return found.title
	if (found?.name) return found.name
	return parsed.kind === 'category' ? `Category #${parsed.id}` : `Item #${parsed.id}`
}

/**
 * Replace every raw ref inside free text with its label: the paywall names
 * item grants "Manual access (item:36)" and some titles embed refs the same
 * way. "Manual access (item:36)" → "Manual access (Backstage tour)".
 */
export function humanizeRefs(text, lists = {}) {
	if (text === null || text === undefined) return ''
	return String(text).replace(/\b(item|channel|category):(\d+)\b/g, (ref) => refLabel(ref, lists))
}

/**
 * Title of the plan / grant a transaction or entitlement is for, with refs
 * resolved. Falls back to the item refs it unlocks, then to a generic label.
 */
export function productTitle(tx, lists = {}, fallback = 'Removed plan') {
	if (!tx) return fallback
	const raw = tx.product?.title || tx.product_title || tx.title
	if (raw) return humanizeRefs(raw, lists)
	const refs = tx.item_refs || tx.items || tx.product?.items || []
	if (refs.length) return refs.map((r) => refLabel(r, lists)).join(', ')
	return tx.product?.public_id ? `Plan ${tx.product.public_id}` : fallback
}

/**
 * Promo code applied to a transaction, when castr forwards it. The paywall
 * stores `invoices.promo_code_id` / `discount_amount`; castr's row (§6e) does
 * not expose the code yet, so this reads the likely shapes and returns null
 * when none is present.
 */
export function promoCodeOf(tx) {
	if (!tx) return null
	const code = tx.promo_code || tx.promo?.code || tx.coupon_code || tx.coupon?.code || null
	return typeof code === 'string' && code.trim() ? code.trim() : null
}

/** Discount recorded on a transaction (castr `discount_amount` when present). */
export function discountOf(tx) {
	const n = Number(tx?.discount_amount ?? tx?.promo?.discount_amount)
	return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Overview semantics (castr §6e): `count` = every non-pending invoice incl.
 * free grants / 100 % promos, `recent` = `amount > 0` only. "Purchases" on
 * the dashboard means the paid ones, so the headline count, the chart header
 * and the Recent purchases list agree.
 */
export function paidPurchaseCount(summary) {
	if (!summary || !Number.isFinite(Number(summary.count))) return null
	const free = Number.isFinite(Number(summary.free_count)) ? Number(summary.free_count) : 0
	return Math.max(0, Number(summary.count) - free)
}

/** True when any bucket of a `summary.series` carries money (collected or refunded). */
export function seriesHasRevenue(series) {
	return (Array.isArray(series) ? series : []).some((p) => Number(p?.gross) > 0 || Number(p?.net) > 0 || Number(p?.refunded) > 0)
}

/** Comma list for castr's `status=` filter, or undefined for "all". */
export function statusParam(selected) {
	const list = (Array.isArray(selected) ? selected : [selected]).filter((s) => TX_STATUSES.includes(s))
	return list.length && list.length < TX_STATUSES.length ? list.join(',') : undefined
}
